import { useQuery } from "@tanstack/react-query";
import type { CatalogResponse, PlatformInfo } from "@baze/api-client";
import { api } from "./api";
import { qk } from "./query";

/** Server platform info. `googleEnabled` may be added by the API later; read defensively. */
export type Platform = PlatformInfo & { googleEnabled?: boolean };

export function usePlatform() {
  return useQuery({
    queryKey: qk.platform,
    queryFn: () => api.public.platform() as Promise<Platform>,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Whether AI features are offered (Admin → Pengaturan → AI). True while loading, so nothing flickers away. */
export function useAiEnabled() {
  return usePlatform().data?.aiEnabled !== false;
}

export function useCatalog() {
  return useQuery({
    queryKey: qk.catalog,
    queryFn: () => api.catalog.get(),
    staleTime: 10 * 60_000,
    gcTime: 60 * 60_000,
  });
}

export function useSite(id: string | undefined) {
  return useQuery({
    queryKey: qk.sites.detail(id ?? ""),
    queryFn: () => api.sites.get(id!),
    enabled: !!id,
  });
}

export function useSites() {
  return useQuery({ queryKey: qk.sites.list, queryFn: () => api.sites.list() });
}

export function useDraft(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.sites.draft(id ?? ""),
    queryFn: () => api.sites.draft(id!),
    enabled: !!id && enabled,
    staleTime: 15_000,
  });
}

export type Catalog = CatalogResponse;
